/**
 * Forgetting Curve Daily Game widget — UI Wave B.
 *
 * Picks the SINGLE most-at-risk card today (lowest predicted retrievability
 * among cards due in the next 7 days) and surfaces it as a "Save it now"
 * mini-game. Turns FSRS science into a daily action.
 *
 * Backend reuses the existing /api/student/trouble-cards endpoint (already
 * sorted by lapses) for the candidate, then fetches its forgetting curve
 * to pick a representative future point for the copy.
 *
 * Dismissable for the day (localStorage key) so it doesn't nag.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, BrainCircuit, Zap } from "lucide-react";

const DISMISS_KEY = "pm_fc_game_dismissed";

function isDismissedToday() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    return v === new Date().toISOString().slice(0, 10);
  } catch {
    return false;
  }
}

function dismissForToday() {
  try { localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10)); } catch { /* ignore */ }
}

export default function ForgettingCurveDailyGame() {
  const [card, setCard] = useState(null);
  const [curve, setCurve] = useState(null);
  const [dismissed, setDismissed] = useState(isDismissedToday);
  const [closing, setClosing] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/student/trouble-cards?limit=1");
        if (cancelled) return;
        const top = r.data?.trouble_cards?.[0];
        if (!top) return;
        setCard(top);
        const cv = await api.get(`/student/forgetting-curve/${top.question_id}?horizon_days=14`);
        if (!cancelled) setCurve(cv.data);
      } catch { /* widget is best-effort — silent fail */ }
    })();
    return () => { cancelled = true; };
  }, [dismissed]);

  if (dismissed || !card) return null;

  // Find the first day where retrievability dips below 70%
  const dipPoint = (curve?.series || []).find((p) => p.retrievability < 0.7);
  const daysUntilDip = dipPoint?.day_offset != null ? Math.max(0, dipPoint.day_offset) : null;

  const close = () => {
    setClosing(true);
    dismissForToday();
    setTimeout(() => setDismissed(true), 280);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-brand-200 dark:border-brand-700/40 fc-game-card transition-all duration-300 ${
        closing ? "scale-95 opacity-0 max-h-0 my-0 py-0" : "max-h-96 p-5"
      }`}
      data-testid="forgetting-curve-daily-game"
    >
      <button
        type="button"
        aria-label="Dismiss for today"
        onClick={close}
        className="absolute top-2 right-2 text-text-muted hover:text-text-primary p-1 transition-colors"
        data-testid="fc-game-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 grid place-items-center shrink-0">
          <BrainCircuit className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-sm tracking-tight text-text-primary">
            Daily Quick Save
          </h3>
          <p className="text-[12px] text-text-secondary mt-1 leading-snug" data-testid="fc-game-copy">
            {daysUntilDip != null && daysUntilDip > 0 ? (
              <>This card will drop below 70% recall in <strong>{daysUntilDip} day{daysUntilDip === 1 ? "" : "s"}</strong>. Save it now?</>
            ) : (
              <>This card is already past its peak retention. One quick rep brings it back.</>
            )}
          </p>
          <blockquote className="mt-2 pl-3 border-l-2 border-brand-300 dark:border-brand-700/60 text-[12px] text-text-secondary italic line-clamp-2">
            "{card.question_preview}"
          </blockquote>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button
              size="sm"
              className="h-8 bg-brand-900 hover:bg-brand-800 text-white text-xs px-3 active:scale-[0.97]"
              onClick={() => nav("/student/smart-review")}
              data-testid="fc-game-cta-btn"
            >
              <Zap className="w-3 h-3 mr-1" /> Quick Save
            </Button>
            {card.subject_name && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">{card.subject_name}</span>
            )}
            {card.lapses_count > 0 && (
              <span className="text-[10px] text-rose-700 dark:text-rose-400">🪤 {card.lapses_count} lapses</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
